"use client";

// react/column-helpers.ts — функции, которым нужен тип ColumnDef из @tanstack/react-table.
// Остальная чистая логика фильтров лежит в app/ и не зависит от React.

import type { ColumnDef } from "@tanstack/react-table";
import {
  PROCESSING_MODE_OBJ,
  type FilterClause,
  type SelectList,
  type SelectListItem,
} from "../domain";
import "./column-meta";

export function getColumnName<TData>(column: ColumnDef<TData>): string {
  const accessorKey =
    "accessorKey" in column && typeof column.accessorKey === "string"
      ? column.accessorKey
      : undefined;
  return column.id ?? accessorKey ?? String(column.header ?? "");
}

export function getListForColumn<TData>(
  lists: SelectList[] | undefined,
  column: ColumnDef<TData>,
): SelectListItem[] | undefined {
  const key = getColumnName(column);
  return lists?.find((l) => l.propertyKey === key)?.items;
}

/**
 * Resolve the filter type for a column.
 *
 * Priority:
 * 1. meta.filterType — explicit override (full authority, including "select-single")
 * 2. meta.filterable === "none" → null (not filterable)
 * 3. Has list items or selectOptions:
 *    - select-multi by default
 *    - select-single only if meta.multiSelect === false (explicit opt-out)
 * 4. Otherwise → text
 */
export function resolveFilterType<TData>(
  column: ColumnDef<TData>,
  lists?: SelectList[],
): FilterClause["type"] | null {
  const meta = column.meta;
  if (meta?.filterType) return meta.filterType;
  if (meta?.filterable === PROCESSING_MODE_OBJ.NONE) return null;
  const listItems = getListForColumn(lists, column);
  if (listItems || meta?.selectOptions) {
    return meta?.multiSelect === false ? "select-single" : "select-multi";
  }
  return "text";
}
